/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Assessment from '@linc-technologies/linced-common/models/assessments/assessment';
import type School from '@linc-technologies/linced-common/models/school';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: assessments.AssessmentCategory.
export default class Category extends Model {
  declare [Type]: 'assessments/category';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('assessments/category', { async: true, inverse: 'subcategories' })
  declare parentCategoryId: AsyncBelongsTo<Category>;

  @alias('parentCategoryId')
  declare parent: AsyncBelongsTo<Category>;

  @alias('parent.id')
  declare parent_id: string | null | undefined;

  @hasMany('assessments/category', { async: true, inverse: 'parentCategoryId' })
  declare subcategories: AsyncHasMany<Category>;

  @computed('subcategories.content.[]')
  get subcategoriesArray() {
    return resolved(this.subcategories);
  }

  subcategorySortOrder = Object.freeze(['sequence', 'label']);

  @sort('subcategoriesArray', 'subcategorySortOrder')
  declare sortedSubcategories: unknown[];

  @computed('sortedSubcategories.[]')
  get sortedBoundSubcategories() {
    // drag-sort reads the owner from the list.
    const subcategories: unknown[] & { __parent__?: Category } =
      this.sortedSubcategories.slice();
    subcategories.__parent__ = this;
    return subcategories;
  }

  @hasMany('assessments/assessment', { async: true, inverse: 'categoryId' })
  declare assessments: AsyncHasMany<Assessment>;

  @computed('assessments.content.[]')
  get assessmentsArray() {
    return resolved(this.assessments);
  }

  assessmentSortOrder = Object.freeze(['sequence', 'label']);

  @sort('assessmentsArray', 'assessmentSortOrder')
  declare sortedAssessments: unknown[];

  @computed('sortedAssessments.[]')
  get sortedBoundAssessments() {
    // drag-sort reads the owner from the list.
    const assessments: unknown[] & { __category__?: Category } =
      this.sortedAssessments.slice();
    assessments.__category__ = this;
    return assessments;
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string; // TODO: implement once Templates are added

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare key: string;
}
